import React from "react";
import { TIntroducerBankDetailsForm, TIntroducerExistingAgreementForm, TIntroducerForm, TIntroducerUploadAgreementForm } from "../../../../../domain/codecs/form/IntroducerForm";
import { TCreateIntroducerApiTokenForm, TIntroducerApiTokenView } from "../../../../../domain/codecs/form/IntroducerApiTokenForm";
import { onChangeForm, onChangeFormBulk } from "../../../../../shared/src/codecs/types/form";
import { sanitiseTypedIntroducerSlug, slugifyIntroducerName } from "../../../../../domain/introducers/slugifyIntroducerName";
import { nameErrorMessage } from "./nameErrorMessage";
import { slugErrorMessage } from "./slugErrorMessage";
import { slugFollowsName } from "./slugFollowsName";
import { formatDate } from "../../../../../shared/src/utilsByDomain/dateTime";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { Padding } from "../../BuildingBlocks/Padding";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonTertiary } from "../../CRMButtonTertiary/CRMButtonTertiary";
import { CRMDropdownComponent } from "../../CRMDropdownComponent/CRMDropdownComponent";
import { CRMFormButton } from "../../CRMFormButton/CRMFormButton";
import { CRMInputCalendarComponent } from "../../CRMInputCalendarComponent/CRMInputCalendarComponent";
import { CRMInputCurrency } from "../../CRMInputCurrency/CRMInputCurrency";
import CRMInputGeneralComponent from "../../CRMInputs/CRMInputGeneralComponent/CRMInputGeneralComponent";
import CRMInputLabelAndErrorWrapComponent from "../../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMMultiUploadBox } from "../../CRMMultiUploadBox/CRMMultiUploadBox";
import { CRMRadioListComponent } from "../../CRMInputs/CRMRadioListComponent/CRMRadioListComponent";
import { CRMMultiSelectDropdownComponent } from "../../CRMMultiSelectDropdownComponent/CRMMultiSelectDropdownComponent";
import { INTRODUCER_API_CAPABILITY_OPTIONS } from "../../../../../domain/codecs/introducerApi/introducerApiCapabilities";
import { ValuationRoute1_DisplayString } from "../../../../../domain/codecs/ValuationRoute";
import * as ListingReasonForSale1 from "../../../../../domain/models/ListingReasonForSale1";
import { INTRODUCER_LOGO_MIME_TYPES, TIntroducerLogoFileIO } from "../../../../../domain/codecs/fileIO/IntroducerLogoFileIO";
import { logoErrorMessage } from "./logoErrorMessage";
import { CRMTitleSubSection } from "../../CRMTitleSubSection/CRMTitleSubSection";
import { CRMUploadedBox } from "../../Simple/CRMUploadedBox/CRMUploadedBox";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { CRMClickCopyWrap } from "../../Simple/CRMClickCopyWrap/CRMClickCopyWrap";
import { WeightBold } from "../../WeightBold/WeightBold";
import { getIntroducerStatus } from "../CRMIntroducerSingleView/CRMIntroducerSingleView";
import { defaultSearchProviderFullName } from "../../../../../domain/defaultSearchProvider";
import { CRMInputPercentage } from "../../CRMInputPercentage/CRMInputPercentage";

type TIntroducerFormProps = {
    form: TIntroducerForm;
    onDeleteUploadAgreement: (form: TIntroducerExistingAgreementForm) => void;
    onUploadAgreement: (form: TIntroducerUploadAgreementForm) => void;
    onUploadLogo: (form: TIntroducerLogoFileIO) => void;
    onChange: (form: TIntroducerForm) => void;
    onChangeBankDetails: (form: TIntroducerBankDetailsForm) => void;
    onSaveBankDetails: (form: TIntroducerBankDetailsForm) => void;
    onChangeApiTokenForm: (form: TCreateIntroducerApiTokenForm) => void;
    onCreateApiToken: (form: TCreateIntroducerApiTokenForm) => void;
    onRevokeApiToken: (token: TIntroducerApiTokenView) => void;
};

export const CRMIntroducerForm = (props: React.PropsWithChildren<TIntroducerFormProps>): JSX.Element => {

    const onChange = onChangeForm(props.form, props.onChange);
    const onChangeBulk = onChangeFormBulk(props.form, props.onChange);

    const onChangeName = (name: string) =>
        slugFollowsName(props.form.original.slug, props.form.edited.slug, props.form.edited.name)
            ? onChangeBulk({ name, slug: slugifyIntroducerName(name) || null })
            : onChange("name")(name);

    const nameError = nameErrorMessage(props.form.validationErrors);
    const slugError = slugErrorMessage(props.form.validationErrors);

    const bankDetailsForm = props.form.children.bank_details_form;
    const canEditBankDetails = props.form.children.can_edit_bank_details;
    const onChangeBankDetails = onChangeForm(bankDetailsForm, props.onChangeBankDetails);

    const createApiTokenForm = props.form.children.create_api_token_form;

    const returnedToken = createApiTokenForm.children.returned_token;

    const onDeleteUploadAgreement = () =>
        props.onDeleteUploadAgreement(props.form.children.existing_agreement_form as TIntroducerExistingAgreementForm)
    ;

    const logoError = logoErrorMessage(props.form.children.logo_upload_form.validationErrors);

    const onUploadLogo = (files: Array<File>) => {
        if (files.length !== 1) {
            return;
        }

        const lastDot = files[0].name.lastIndexOf(".");

        props.onUploadLogo({
            ...props.form.children.logo_upload_form,
            status: "requiresSubmission",
            input: {
                ...props.form.children.logo_upload_form.input,
                id: props.form.edited.id,
                file_extension: lastDot >= 0 ? files[0].name.slice(lastDot + 1) : "",
                mime_type: files[0].type,
            },
            clientSideFileForUpload: files[0],
        });
    };

    const onRemoveLogo = () =>
        props.onChange({
            ...props.form,
            status: "requiresSubmission",
            edited: { ...props.form.edited, brand_logo_url: null },
        });

    const onUploadAgreement = (files: Array<File>) => {

        if (files.length !== 1) {
            return;
        }

        const indexOfLastFullStopInFileName = files[0].name.lastIndexOf(".");

        props.onUploadAgreement({
            ...props.form.children.upload_form,
            status: "requiresSubmission",
            input: {
                ...props.form.children.upload_form.input,
                file_extension: indexOfLastFullStopInFileName >= 0 ? files[0].name.slice(indexOfLastFullStopInFileName + 1) : "",
                mime_type: files[0].type === "" ? "text/plain" : files[0].type,
            },
            clientSideFileForUpload: files[0],
        });
    };

    return (
        <Padding 
            width="100%" 
            type="bottom" 
            spacing={"60px"}
        >
            <SpacingColumn spacing={CRMSpacing.X_LARGE}>
                
                {/* SECTION - BASIC INFO */}
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    {/* INPUT - NAME */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Introducer name"
                        displayError={nameError !== null}
                        errorMessage={nameError || ""}
                    >
                        <CRMInputGeneralComponent
                            value={props.form.edited.name}
                            inputType="text"
                            placeholder="Name"
                            onChange={onChangeName}
                            displayError={nameError !== null}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - SLUG */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="URL slug"
                        guidance="This is their portal address and their partner page. Change it and any link they have shared will stop working."
                        displayError={slugError !== null}
                        errorMessage={slugError || ""}
                    >
                        <CRMInputGeneralComponent
                            value={props.form.edited.slug || ""}
                            inputType="text"
                            placeholder="e.g. estate-search"
                            onChange={(value) => onChange("slug")(sanitiseTypedIntroducerSlug(value) || null)}
                            displayError={slugError !== null}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* BRAND LOGO */}
                    {/* Sits next to the slug because the two together are what a partner
                        landing page needs. Without a logo the lockup falls back to the
                        partner's name in text, which works but is not what a co-branded
                        page is for. */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Brand logo"
                        guidance="Shown next to the Sail logo on their partner page. PNG, JPEG or WebP, under 2MB. Without one the page shows their name as text instead."
                        displayError={logoError !== null}
                        errorMessage={logoError || ""}
                    >
                        {props.form.edited.brand_logo_url
                            ? <CRMUploadedBox
                                label="Brand logo"
                                onDelete={onRemoveLogo}
                                link={props.form.edited.brand_logo_url}
                            />
                            : <CRMMultiUploadBox
                                label="Upload brand logo"
                                accept={INTRODUCER_LOGO_MIME_TYPES.join(",")}
                                onFileChange={onUploadLogo}
                            />}
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - KLYANT CLIENT ID */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Klyant client ID"
                    >
                        <CRMInputGeneralComponent
                            value={props.form.edited.klyant_client_id}
                            inputType="text"
                            placeholder="ID"
                            onChange={onChange("klyant_client_id")}
                            displayError={false} 
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - EMAIL DOMAIN */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Email domains (for automatic Front webhook matching, comma-separated)"
                    >
                        <CRMInputGeneralComponent
                            value={props.form.edited.email_domain || ""}
                            inputType="text"
                            placeholder="e.g. new-law.co.uk,other-domain.co.uk"
                            onChange={(value) => onChange("email_domain")(value ? value.replace(/\s/g, "") : null)}
                            displayError={false}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - REFERRAL FEE & UPLOAD */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Is this introducer being paid a referral fee?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.is_paid_referral_fee}
                            options={[
                                { value: true, text: "Yes" },
                                { value: false, text: "No" },
                            ]}
                            onChange={onChange("is_paid_referral_fee")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {props.form.edited.is_paid_referral_fee &&
                        <>
                            {getIntroducerStatus(props.form) === "no-agreement-signed" &&
                                <CRMMultiUploadBox
                                    label="Upload Signed Agreement"
                                    onFileChange={onUploadAgreement}
                                />
                            }

                            {(getIntroducerStatus(props.form) === "agreement-signed"
                                || getIntroducerStatus(props.form) === "agreement-requires-review")
                                && <CRMUploadedBox
                                    label="Agreement Document"
                                    onDelete={onDeleteUploadAgreement}
                                    link={props.form.children.existing_agreement_form?.children.download_url || ""} 
                                />
                            }
                        </>
                    }
                    
                    {/* INPUT - NEXT REVIEW DATE */}
                    {!!props.form.children.existing_agreement_form?.edited.id &&
                        <CRMInputLabelAndErrorWrapComponent
                            label="Next review due date"
                        >
                            <CRMInputCalendarComponent
                                placeholder="Select date"
                                dateType="date"
                                value={props.form.edited.referral_agreement_next_review_date || ""}
                                displayError={false}
                                onChange={onChange("referral_agreement_next_review_date")}
                                onPressEnterKey={() => null}
                            />
                        </CRMInputLabelAndErrorWrapComponent>
                    }

                    {/* INPUT - ABORTIVE COSTS */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Could a case referred to us by this introducer incur abortive costs?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.will_charge_abortive_costs}
                            options={[
                                { value: true, text: "Yes" },
                                { value: false, text: "No" },
                            ]}
                            onChange={onChange("will_charge_abortive_costs")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - LAND REGISTRY ORDER ENABLED */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Run the title check (and place the £7 Land Registry order) on this introducer's full valuations?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.land_registry_order_enabled}
                            options={[
                                { value: true, text: "Yes" },
                                { value: false, text: "No" },
                            ]}
                            onChange={onChange("land_registry_order_enabled")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - VALUATION ROUTE */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Which valuation do this introducer's leads get?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.valuation_route}
                            options={[
                                { value: "discovery" as const, text: ValuationRoute1_DisplayString("discovery") },
                                { value: "instant" as const, text: ValuationRoute1_DisplayString("instant") },
                            ]}
                            onChange={onChange("valuation_route")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - REFERRAL REASON FOR SALE */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="What kind of sale does this introducer refer?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.referral_reason_for_sale}
                            options={ListingReasonForSale1.values.map((value) => ({
                                value,
                                text: ListingReasonForSale1.fromValueToDisplayName(value),
                            }))}
                            onChange={onChange("referral_reason_for_sale")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - NPS ENABLED */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Send NPS surveys to clients on cases referred by this introducer?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.nps_enabled ?? true}
                            options={[
                                { value: true, text: "Yes" },
                                { value: false, text: "No" },
                            ]}
                            onChange={onChange("nps_enabled")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - WEEKLY UPDATES ENABLED */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Show this introducer's cases in the weekly client-updates list (and CC the introducer on the updates sent)?"
                    >
                        <CRMRadioListComponent
                            value={props.form.edited.weekly_updates_enabled ?? false}
                            options={[
                                { value: true, text: "Yes" },
                                { value: false, text: "No" },
                            ]}
                            onChange={onChange("weekly_updates_enabled")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                </SpacingColumn>

                {/* SECTION - DEFAULT REFERRAL FEES */}
                {props.form.edited.is_paid_referral_fee &&
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <CRMTitleSubSection>
                            Default referral fees
                        </CRMTitleSubSection>

                        {/* INPUT - DEFAULT REFERRAL FEE - PURCHASE */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="What is the default fee we pay for a purchase?"
                        >
                            <CRMInputCurrency
                                value={props.form.edited["default_referral_fee_pence--purchase"] || null}
                                mode="pence"
                                onChange={(value) => onChange("default_referral_fee_pence--purchase")(value || 0)}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        {/* INPUT - DEFAULT REFERRAL FEE - SALE */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="What is the default fee we pay for a sale?"
                        >
                            <CRMInputCurrency
                                value={props.form.edited["default_referral_fee_pence--sale"] || null}
                                mode="pence"
                                onChange={(value) => onChange("default_referral_fee_pence--sale")(value || 0)}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        {/* INPUT - DEFAULT REFERRAL FEE - TRANSFER */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="What is the default fee we pay for a transfer?"
                        >
                            <CRMInputCurrency
                                value={props.form.edited["default_referral_fee_pence--transfer"] || null}
                                mode="pence"
                                onChange={(value) => onChange("default_referral_fee_pence--transfer")(value || 0)}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        {/* INPUT - DEFAULT REFERRAL FEE - REMORTGAGE */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="What is the default fee we pay for a remortgage?"
                        >
                            <CRMInputCurrency
                                value={props.form.edited["default_referral_fee_pence--remortgage"] || null}
                                mode="pence"
                                onChange={(value) => onChange("default_referral_fee_pence--remortgage")(value || 0)}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        {/* INPUT - SAIL HOMES SALE REFERRAL FEE PERCENTAGE */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="What is the Sail Homes accepted offer referral percentage?"
                        >
                            <CRMInputPercentage
                                value={props.form.edited.sail_homes_sale_referral_percentage || null}
                                onChange={(value) => onChange("sail_homes_sale_referral_percentage")(value || 0)}
                            />
                        </CRMInputLabelAndErrorWrapComponent>
                    </SpacingColumn>
                }
                
                {/* SECTION - MONEY ON ACCOUNT */}
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSubSection>
                        Client Initial payment amounts (money on account)
                    </CRMTitleSubSection>

                    {/* INPUT - PURCHASE */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Purchase"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_purchase_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_purchase_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    
                    {/* INPUT - TRANSFER */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Transfer"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_transfer_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_transfer_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - REMORTGAGE */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Remortgage"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_remortgage_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_remortgage_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    
                    {/* INPUT - SALE (WITH LEASEHOLD) */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Sale (with leasehold)"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_sale_with_leasehold_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_sale_with_leasehold_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    
                    {/* INPUT - SALE (WITHOUT LEASEHOLD) */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Sale (without leasehold)"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_sale_without_leasehold_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_sale_without_leasehold_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* INPUT - SALE (WITHOUT LEASEHOLD) */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Additional amount when sale ordering searches"
                    >
                        <CRMInputCurrency
                            value={props.form.edited.initial_payment_additional_seller_searches_amount_pence}
                            mode="pence"
                            onChange={onChange("initial_payment_additional_seller_searches_amount_pence")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                </SpacingColumn>


                {/* SECTION - SEARCHES */}
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSubSection>
                        Searches
                    </CRMTitleSubSection>

                    {/* INPUT - SEARCH ORIGIN */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Where are the searches ordered from?"
                    >
                        <CRMDropdownComponent
                            value={props.form.edited.search_provider}
                            options={[
                                { label: `Our default provider (${defaultSearchProviderFullName})`, value: "default" },
                                { label: "Somewhere Else", value: "somewhere_else" },
                            ]}
                            displayError={false}
                            onChange={onChange("search_provider")}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                    
                    {/* INPUT - SEARCH PROVIDER NAME */}
                    {props.form.edited.search_provider === "somewhere_else" &&<>
                        <CRMInputLabelAndErrorWrapComponent
                            label="Search provider name"
                        >
                            <CRMInputGeneralComponent
                                value={props.form.edited.custom_search_provider_name}
                                inputType="text"
                                placeholder="Name"
                                onChange={onChange("custom_search_provider_name")}
                                displayError={false} 
                            />
                        </CRMInputLabelAndErrorWrapComponent>
                    
                        {/* INPUT - SEARCH PROVIDER URL */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="Search provider URL"
                        >
                            <CRMInputGeneralComponent
                                value={props.form.edited.custom_search_provider_url}
                                inputType="text"
                                placeholder="URL"
                                onChange={onChange("custom_search_provider_url")}
                                displayError={false}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        {/* INPUT - SEARCH ORDERING NOTES */}
                        <CRMInputLabelAndErrorWrapComponent
                            label="Search ordering notes"
                        >
                            <CRMInputGeneralComponent
                                value={props.form.edited.custom_search_ordering_notes}
                                inputType="text"
                                placeholder="Notes"
                                onChange={onChange("custom_search_ordering_notes")}
                                displayError={false}
                            />
                        </CRMInputLabelAndErrorWrapComponent>
                    </>}
                </SpacingColumn>

                {/* SECTION - BANK DETAILS */}
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSubSection>
                        Bank details
                    </CRMTitleSubSection>

                    <CRMParagraph>
                        Where case funds are returned when this introducer is chosen as the payout recipient.
                        {canEditBankDetails
                            ? " These are auto-filled onto the case — the client never re-enters them."
                            : " Only authorised staff can change these."
                        }
                    </CRMParagraph>

                    <CRMInputLabelAndErrorWrapComponent label="Account holder name (as it appears on the bank account)">
                        <CRMInputGeneralComponent
                            value={bankDetailsForm.edited.bank_account_holder_name}
                            inputType="text"
                            placeholder="Account holder name"
                            disabled={!canEditBankDetails}
                            onChange={onChangeBankDetails("bank_account_holder_name")}
                            displayError={false}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    <CRMInputLabelAndErrorWrapComponent label="Bank name">
                        <CRMInputGeneralComponent
                            value={bankDetailsForm.edited.bank_name}
                            inputType="text"
                            placeholder="Bank name"
                            disabled={!canEditBankDetails}
                            onChange={onChangeBankDetails("bank_name")}
                            displayError={false}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    <CRMInputLabelAndErrorWrapComponent label="Account number">
                        <CRMInputGeneralComponent
                            value={bankDetailsForm.edited.account_number}
                            inputType="numericString"
                            placeholder="Account number"
                            disabled={!canEditBankDetails}
                            onChange={onChangeBankDetails("account_number")}
                            displayError={false}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    <CRMInputLabelAndErrorWrapComponent label="Sort code">
                        <CRMInputGeneralComponent
                            value={bankDetailsForm.edited.sort_code}
                            inputType="numericString"
                            placeholder="Sort code (6 digits)"
                            disabled={!canEditBankDetails}
                            onChange={onChangeBankDetails("sort_code")}
                            displayError={false}
                        />
                    </CRMInputLabelAndErrorWrapComponent>

                    {canEditBankDetails &&
                        <CRMFormButton
                            formStatus={bankDetailsForm.status}
                            ButtonElement={CRMButtonPrimary}
                            label="Save bank details"
                            onClick={() => props.onSaveBankDetails(bankDetailsForm)}
                        />
                    }
                </SpacingColumn>

                {/* SECTION - API TOKENS */}
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSubSection>
                        API tokens
                    </CRMTitleSubSection>

                    {/* LIST - EXISTING TOKENS */}
                    {props.form.children.api_tokens.length === 0 &&
                        <CRMParagraph>
                            No API tokens have been generated for this introducer.
                        </CRMParagraph>
                    }

                    <SpacingColumn spacing={CRMSpacing.SMALL}>
                        {props.form.children.api_tokens.map((token) =>
                            <Background
                                key={token.jti}
                                color={CRMColors.NEUTRAL_PAPER}
                                borderDefinition={`1px solid ${CRMColors.NEUTRAL_8}`}
                                borderRadius="5px"
                                padding={CRMSpacing.SMALL}
                            >
                                <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                    <CRMParagraph>
                                        <WeightBold>{token.label || "Untitled token"}</WeightBold>
                                        {token.is_test_mode ? " · Test mode" : ""}
                                        {" · "}
                                        {token.capabilities.length === 0
                                            ? "Full access"
                                            : token.capabilities.join(", ")
                                        }
                                        {" · "}
                                        Created {formatDate(token.created_at, "DD")}
                                        {" · "}
                                        {token.last_used_at
                                            ? `Last used ${formatDate(token.last_used_at, "DD")}`
                                            : "Never used"
                                        }
                                    </CRMParagraph>
                                    <CRMButtonTertiary
                                        icon="delete"
                                        label="Revoke"
                                        highlight="instructional-brick"
                                        onClick={() => props.onRevokeApiToken(token)}
                                    />
                                </SpacingRow>
                            </Background>
                        )}
                    </SpacingColumn>

                    {/* SUB-FORM - GENERATE TOKEN */}
                    <CRMInputLabelAndErrorWrapComponent
                        label="Generate API token"
                    >
                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            <CRMInputGeneralComponent
                                value={createApiTokenForm.edited.label}
                                inputType="text"
                                placeholder="Token label (e.g. integration name)"
                                onChange={(value) => props.onChangeApiTokenForm({
                                    ...createApiTokenForm,
                                    status: "requiresSubmission",
                                    edited: {
                                        ...createApiTokenForm.edited,
                                        label: value || "",
                                    },
                                })}
                                displayError={false}
                            />

                            {/* Empty selection = full/unscoped access (legacy tokens). */}
                            <CRMInputLabelAndErrorWrapComponent label="Capabilities (leave empty for full access)">
                                <CRMMultiSelectDropdownComponent
                                    value={createApiTokenForm.edited.capabilities}
                                    options={INTRODUCER_API_CAPABILITY_OPTIONS}
                                    placeholder="Full access"
                                    onChange={(capabilities) => props.onChangeApiTokenForm({
                                        ...createApiTokenForm,
                                        status: "requiresSubmission",
                                        edited: {
                                            ...createApiTokenForm.edited,
                                            capabilities,
                                        },
                                    })}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            <CRMInputLabelAndErrorWrapComponent label="Test-mode token?">
                                <CRMRadioListComponent
                                    value={createApiTokenForm.edited.is_test_mode}
                                    options={[
                                        { value: false, text: "No (live)" },
                                        { value: true, text: "Yes (test)" },
                                    ]}
                                    onChange={(is_test_mode) => props.onChangeApiTokenForm({
                                        ...createApiTokenForm,
                                        status: "requiresSubmission",
                                        edited: {
                                            ...createApiTokenForm.edited,
                                            is_test_mode,
                                        },
                                    })}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            <CRMFormButton
                                formStatus={createApiTokenForm.status}
                                ButtonElement={CRMButtonPrimary}
                                label="Generate"
                                onClick={() => props.onCreateApiToken(createApiTokenForm)}
                            />
                        </SpacingColumn>
                    </CRMInputLabelAndErrorWrapComponent>

                    {/* SHOW-ONCE - RETURNED TOKEN */}
                    {returnedToken &&
                        <Background
                            color={CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_10}
                            borderDefinition={`1px solid ${CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_2}`}
                            borderRadius="5px"
                            padding={CRMSpacing.MEDIUM}
                        >
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <CRMParagraph>
                                    <WeightBold>Copy this token now</WeightBold> — it won't be shown again.
                                </CRMParagraph>
                                <CRMClickCopyWrap value={returnedToken}>
                                    <div style={{wordBreak: "break-all"}}>
                                        <CRMParagraph>{returnedToken}</CRMParagraph>
                                    </div>
                                </CRMClickCopyWrap>
                            </SpacingColumn>
                        </Background>
                    }
                </SpacingColumn>

            </SpacingColumn>
        </Padding>
    );
};
